import clsx from 'clsx';
import {useFormik} from 'formik';
import {useState} from 'react';
import {useNavigate, Link} from 'react-router-dom';
import * as Yup from 'yup';

import {useAuthContext} from '@/auth/useAuthContext';
import {KeenIcon} from '@/components';
import {useLayout} from '@/providers';
import {useSnackbar} from 'notistack';

const initialValues = {
    email: ''
};

const forgotPasswordSchema = Yup.object().shape({
    email: Yup.string()
        .email('Wrong email format')
        .min(3, 'Minimum 3 symbols')
        .max(50, 'Maximum 50 symbols')
        .required('Email is required')
});

const ResetPassword = () => {
    const [loading, setLoading] = useState(false);
    const {requestPassword} = useAuthContext();
    const {currentLayout} = useLayout();
    const {enqueueSnackbar} = useSnackbar(); // Snackbar for notifications
    const navigate = useNavigate(); // Navigation hook for redirecting

    const formik = useFormik({
        initialValues,
        validationSchema: forgotPasswordSchema,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                if (!requestPassword) {
                    throw new Error('JWTProvider is required for this form.');
                }

                const success = await requestPassword(values.email);

                if (success) {
                    navigate('/auth/reset-password/check-email'); // Redirect on success
                }

            } catch (error) {

            } finally {
                setLoading(false);
            }
        }
    });

    return (
        <div className="card max-w-[370px] w-full">
            <form
                className="card-body flex flex-col gap-5 p-10"
                noValidate
                onSubmit={formik.handleSubmit}
            >
                <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900">Password Reset</h3>
                    <span className="text-2sm text-gray-600 font-medium">
                        Enter your email to reset password.
                    </span>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="form-label text-gray-900">Email</label>
                    <label className="input">
                        <input
                            type="email"
                            placeholder="email@email.com"
                            autoComplete="off"
                            {...formik.getFieldProps('email')}
                            className={clsx(
                                'form-control bg-transparent',
                                {'is-invalid': formik.touched.email && formik.errors.email},
                                {'is-valid': formik.touched.email && !formik.errors.email}
                            )}
                        />
                    </label>
                    {formik.touched.email && formik.errors.email && (
                        <span role="alert" className="text-danger text-xs mt-1">
                            {formik.errors.email}
                        </span>
                    )}
                </div>

                <div className="flex flex-col gap-5 items-stretch">
                    <button
                        type="submit"
                        className="btn btn-primary flex justify-center grow"
                        disabled={loading}
                    >
                        {loading ? 'Please wait...' : 'Continue'}
                    </button>

                    <Link
                        to={currentLayout?.name === 'auth-branded' ? '/auth/login' : '/auth/classic/login'}
                        className="flex items-center justify-center text-sm gap-2 text-gray-700 hover:text-primary"
                    >
                        <KeenIcon icon="black-left"/>
                        Back to Login
                    </Link>
                </div>
            </form>
        </div>
    );
};

export {ResetPassword};
